import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import {
  Text,
  Box,
  Button,
  CMSContent,
  Icon,
  ClickableContent,
  Dropdown,
} from '@deseretbook/react-ui'
import { useDropdown, useMediaQuery } from '@deseretbook/react-utils'
import './MediaCard.css'
import MediaCard from './MediaCard'
import getMediaUrl from '../../../utils/mediaUrl'
import { detailsUrl, listenAudiobookUrl } from '../../../utils/routes'
import trimHtml from '../../../utils/trimHtml'
import { BOOKSHELF_PLUS_IDS, DIGITAL_VIP_IDS } from '../../../subscriptionIds'
import AddToLibrary from '../../buttons/AddToLibrary'
import useUserSub from '../../../hooks/useUserSub'
import AuthorLinks from '../authorLinks/AuthorLinks'
import { openPopupPlayer } from '../../../utils/media'
import CopyTooltip from '../../tooltips/CopyTooltip'
import checkMediaSubscription from '../../../utils/checkMediaSub'
import PlayerWrapper from './PlayerWrapper'

/**
 * A component for displaying a media object.
 *
 * @param {object} props - component props
 * @returns {function} Component
 */
function MediaCardFull(props) {
  const {
    className,
    media: defaultMedia,
    mediaType,
    updateMedia,
    variantSku,
    isDiscover,
    ...rest
  } = props

  const isTabletOrSmaller = useMediaQuery({ maxWidth: 780 })

  const dropdown = useDropdown()
  const { userPlus, userVip } = useUserSub()
  const [media, setMedia] = useState(defaultMedia)

  // Applies flexbox styles to arrange figure and descriptions
  const mediaCardContainer = classNames('MediaCardContainer', className)
  // Applies base styling to ensure figure dimensions
  const mediaCardItem = classNames('MediaCardItem')
  const linkClasses = classNames('MediaCardLink', className)
  const linkContainerClasses = classNames('MediaCardLinkContainer', className)

  // Set media types
  const isEbook = media && media.ebook
  const isAudiobook = media && media.audiobook

  // Subscription IDs
  const plusIds = BOOKSHELF_PLUS_IDS
  const vipIds = DIGITAL_VIP_IDS

  // Collection of variables to be used in the component
  const isPlus = checkMediaSubscription(media[mediaType], plusIds)
  const isVip = checkMediaSubscription(media[mediaType], vipIds)
  const isFree = (
    (media && media[mediaType] && media[mediaType].subscriptionPlanIds) ||
    []
  ).includes('free')

  const sku = (media[mediaType] && media[mediaType].sku) || variantSku
  const landingPage = detailsUrl(media.mediaId, sku)

  /**
   * Handles updating the media object returned from the API
   *
   * @param {object} newMedia - The updated media object.
   * @param {number} id - The id of the media to match.
   * @param {string} sku - The sku of the media to match.
   */
  const handleUpdateMedia = (newMedia, id, sku) => {
    const formattedMedia = Object.assign({}, newMedia)

    // Remove ebook or audiobook from media object
    if (mediaType === 'ebook') {
      formattedMedia.audiobook = null
    } else {
      formattedMedia.ebook = null
    }

    updateMedia(formattedMedia, id, sku)
    setMedia(formattedMedia)
  }

  const { inLibrary = false, subscribed = false, purchased = false, subscribable = false } =
    (media[mediaType] && media[mediaType].userInfo) || {}

  const titleInfo = (
    <Box isColumn marginRight={2}>
      <PlayerWrapper
        isAudiobook={media && media.audiobook}
        media={media}
        className="MediaCardTitle"
        landingPage={landingPage}
        inLibrary={inLibrary || subscribed}
        isDiscover={isDiscover}
        style={{
          marginBottom: '0.25rem',
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 700,
          fontSize: '20px',
          color: 'black',
          width: '100%',
          cursor: 'pointer',
        }}
      >
        {media.title}
      </PlayerWrapper>

      {media && media.authors.length && (
        <AuthorLinks style={{ fontSize: '16px' }} authors={media.authors} />
      )}
    </Box>
  )

  let buttonColor = 'bookshelf'

  if (!purchased || inLibrary) {
    if ((isPlus || isVip) && userVip) {
      buttonColor = 'vip'
    } else if (isPlus && userPlus) {
      buttonColor = 'plus'
    }
  }

  let showAddToLibrary = false

  if (!(inLibrary || subscribed) && subscribable) {
    if (isFree) {
      showAddToLibrary = true
    } else if ((userVip && (isVip || isPlus)) || (userPlus && isPlus)) {
      showAddToLibrary = true
    }
  }

  const showActNowEbook = !showAddToLibrary && (inLibrary || subscribed) && isEbook
  const showActNowAudiobook = !showAddToLibrary && (inLibrary || subscribed) && isAudiobook

  const iconStyles = {
    borderRadius: '1rem',
    backgroundColor: '#B6BFBF26',
    padding: '0.5rem',
    width: '36px',
    height: '36px',
    marginRight: '8px',
  }

  const dbcomUrl = (media && media[mediaType] && media[mediaType].productUrl) || ''
  const htmlDescription = media.description ? trimHtml(media.description, 500) : ''

  return (
    <>
      <div
        className={mediaCardContainer}
        {...rest}
        style={{ flexDirection: isTabletOrSmaller ? 'column' : 'row' }}
      >
        <MediaCard
          {...props}
          className={mediaCardItem}
          hideOptions
          isPlus={isPlus}
          isVip={isVip}
          isDiscover={isDiscover}
        />

        <Box alignItems="start" justifyContent="start" isColumn isFullwidth>
          <Box alignItems="start" justifyContent="space-between" isFullwidth>
            <Box alignItems="start" justifyContent="start">
              {titleInfo}
            </Box>
            <Box alignItems="center">
              <Box
                marginRight={1}
                alignItems="center"
                justifyContent="center"
                style={{ minWidth: '2rem' }}
                {...dropdown.triggerProps}
              >
                <ClickableContent isPaddingless>
                  <div className="dropdown-trigger">
                    <Icon name="options" />
                  </div>
                </ClickableContent>
              </Box>
              {showAddToLibrary && (
                <AddToLibrary
                  isFree={isFree}
                  isPlus={isPlus && userPlus}
                  isVip={(isVip || isPlus) && userVip}
                  mediaId={media.mediaId}
                  updateMedia={handleUpdateMedia}
                  sku={media[mediaType] && media[mediaType].sku}
                />
              )}
              {showActNowEbook && (
                <Button
                  className="act-now-button"
                  as={Link}
                  to={getMediaUrl(media)}
                  color={buttonColor}
                  {...rest}
                >
                  <Text
                    className="act-now-text"
                    style={{
                      color: buttonColor === 'vip' ? 'white' : 'black',
                    }}
                  >
                    Read Now
                  </Text>
                </Button>
              )}
              {showActNowAudiobook && (
                <Button
                  className="act-now-button"
                  onClick={() => {
                    openPopupPlayer(listenAudiobookUrl(media.mediaId, sku))
                  }}
                  color={buttonColor}
                  {...rest}
                >
                  <Text
                    className="act-now-text"
                    style={{
                      color: buttonColor === 'vip' ? 'white' : 'black',
                    }}
                  >
                    Listen Now
                  </Text>
                </Button>
              )}
            </Box>
          </Box>
          <Box
            maxHeight={10}
            style={{
              marginTop: '18px',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              lineHeight: '22px',
              color: '#0000008C',
              fontSize: '16px',
              overflow: 'hidden',
            }}
          >
            <CMSContent
              dangerouslySetInnerHTML={{ __html: htmlDescription }}
              itsNotDangerousBecause="media.description comes directly from the Bookshelf API, and should be trustworthy"
            />
          </Box>
          <Box style={{ marginTop: '16px' }} alignItems="center">
            {isEbook && <Icon name="ebook" style={iconStyles} />}
            {isAudiobook && <Icon name="audiobook" style={iconStyles} />}
            {isPlus && !userVip && (
              <Icon
                name="bookshelfPlusNew"
                style={{
                  width: '123px',
                }}
              />
            )}
            {(isVip || isPlus) && userVip && (
              <Icon
                name="digitalVipNew"
                style={{
                  width: '123px',
                }}
              />
            )}
          </Box>
        </Box>
      </div>
      <Dropdown {...dropdown.dropdownProps} id="MediaCardDropdown">
        <Box isColumn alignItems="start" className={linkContainerClasses}>
          <Link to={landingPage} {...rest} className={linkClasses}>
            <Icon name="info" className="link-icon" />
            <Text>Title Details</Text>
          </Link>
          <CopyTooltip textToCopy={dbcomUrl}>
            <Button {...rest} className={linkClasses} style={{ border: 'none' }}>
              <Icon name="share" className="link-icon" />
              <Text>Share Title</Text>
            </Button>
          </CopyTooltip>
        </Box>
      </Dropdown>
    </>
  )
}

MediaCardFull.defaultProps = {
  className: '',
  media: {},
  mediaType: 'ebook',
  isAudiobook: false,
  isEbook: false,
  isPlus: false,
  updateMedia: () => {},
  isDiscover: false,
}

MediaCardFull.propTypes = {
  /** Additional classes */
  className: PropTypes.string,
  /** Media object to be rendered */
  media: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    authors: PropTypes.arrayOf({}),
    mediaId: PropTypes.string,
  }),
  /** The type of media to access */
  mediaType: PropTypes.string,
  /** If the media is an audiobook */
  isAudiobook: PropTypes.bool,
  /** If the media is an ebook */
  isEbook: PropTypes.bool,
  /** If the media is part of bookshelf+ */
  isPlus: PropTypes.bool,
  updateMedia: PropTypes.func,
  isDiscover: PropTypes.bool,
}

export default MediaCardFull
